import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { contactApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2, Eye, EyeOff, Trash2, Mail } from 'lucide-react';
import { toast } from 'sonner';

const AdminMessages = () => {
  const [data, setData] = useState({ messages: [], total: 0, unread_count: 0 });
  const [loading, setLoading] = useState(true);
  const [filterUnread, setFilterUnread] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const res = await contactApi.getMessages({ unread_only: filterUnread });
      setData(res);
    } catch (error) {
      console.error('Failed to load messages:', error);
      toast.error('Mesajlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUnread]);

  const handleToggleFilter = () => {
    setFilterUnread((prev) => !prev);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu mesajı silmek istediğinize emin misiniz?')) return;
    setDeletingId(id);
    try {
      await contactApi.deleteMessage(id);
      toast.success('Mesaj silindi');
      fetchMessages();
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error('Mesaj silinemedi');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-bold text-foreground">Mesajlar</h1>
          <p className="text-sm text-muted-foreground">
            Ziyaretçilerden gelen iletişim formu mesajlarını görüntüleyin ve yönetin.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">
            Okunmamış: <strong className="text-foreground">{data.unread_count}</strong>
          </span>
          <Button
            type="button"
            variant={filterUnread ? 'default' : 'outline'}
            size="sm"
            onClick={handleToggleFilter}
          >
            {filterUnread ? 'Tüm Mesajlar' : 'Sadece Okunmamış'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="font-serif text-lg">Mesaj Listesi</CardTitle>
        </CardHeader>
        <CardContent>
          {data.messages.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              Henüz mesaj bulunmuyor.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Gönderen</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Konu</TableHead>
                    <TableHead>Tarih</TableHead>
                    <TableHead className="w-[120px] text-right">Durum</TableHead>
                    <TableHead className="w-[120px] text-right">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.messages.map((msg) => (
                    <TableRow key={msg._id} className="hover:bg-muted/40">
                      <TableCell className="font-medium flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span>{msg.name}</span>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {msg.email}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {msg.subject || '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(msg.created_at).toLocaleString('tr-TR')}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant={msg.read ? 'secondary' : 'default'}>
                          {msg.read ? 'Okundu' : 'Okunmadı'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => navigate(`/admin/messages/${msg._id}`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          disabled={deletingId === msg._id}
                          onClick={() => handleDelete(msg._id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMessages;
